import { wssProtocol } from "@/common/config"
import { TableHub } from "../common/tableHub"
import { Object, SimpleObject } from "../model/object"
import { device, deviceLangCode, Devices, isMobile } from "@/common/device"
import { isObject } from "@/common/system"

const languageCodeKey = 'langCode'

export interface LangDataRow {
    id: string
    title: string
} 

export interface LangData {
    [id:string]:LangDataRow
} 

export class I18NRepository {
    langs: Map<string, Translates> 
    #defaultLang: string

    constructor(defaultCode?: string, defaultDevice?: Devices) {
        const savedLangCode = localStorage.getItem(languageCodeKey)
        this.langs = new Map<string, Translates>()
        this.#defaultLang = savedLangCode ?? defaultCode ?? deviceLangCode()
    }

    async get(section: string, langCode?: string):Promise<Translates> {
        const hash = window.document.body.getAttribute('langHash')
        if(langCode===undefined) {
            langCode = this.#defaultLang
        }
        const key = `${langCode}:${section}`
        const translate = this.langs.get(key)
        if(translate !== undefined) {
            return translate
        }
        return fetch(`/i18n/${langCode}/${section}.json?hash=${hash}`).then(async (response): Promise<Translates> => { 
            const result = await response.json()
            const trans = new Translates(result)
            this.langs.set(key, trans)
            return trans
        })
    }

    defaultLang(): string {
        return this.#defaultLang
    }

    setDefaultLang(code: string) {
        localStorage.setItem(languageCodeKey, code)
        this.#defaultLang = code
    }

    languages(): string[] {
        return [
            'pl', // Polish
            'en', // English
            'de', // German 
            'uk', // Ukrainian
            'it', // Italian
            'es', // Spanish
            'lt', // Lithuanian
        ]
    }

    langData(): LangData {
        return {
            pl: {id: 'pl', title: 'Polski'},
            en: {id: 'en', title: 'English'},
            de: {id: 'de', title: 'Deutsch'},
            uk: {id: 'uk', title: 'Український'},
            it: {id: 'it', title: 'Italiano'},
            es: {id: 'es', title: 'Español'},
            lt: {id: 'lt', title: 'Lietuvių'},
        }
    }
}

export class Translates {
    #translates: any

    constructor(translates: any) {
        this.#translates = translates
    }

    #for(path: string):any {
        return path.split('.').reduce((acc, part) => acc && acc[part], this.#translates)
    }

    for(path: string, defaultValue?:string):string {
        if(defaultValue===undefined) {
            defaultValue = path
        }
        return this.#for(path) ?? defaultValue 
    }

    #forDevice(path: string, deviceKey:string, defaultValue?:string): string {
        if(defaultValue===undefined) {
            defaultValue = path
        }
        const row = this.#for(path) 
        if(isObject(row)) {
            return row[deviceKey] ?? defaultValue 
        }
        return row ?? defaultValue
    }

    forMobile(path: string, defaultValue?:string): string {
        return this.#forDevice(path, 'mobile', defaultValue)
    }

    forDesktop(path: string, defaultValue?:string):string {
        return this.#forDevice(path, 'desktop', defaultValue)
    }

    forDevice(path: string, defaultValue?:string): string {
        return isMobile() ? this.forMobile(path, defaultValue): this.forDesktop(path, defaultValue)
    }
}
